import { createStore } from 'vuex';
import svc from '@/core/svc-client';
import { get as onboardingGetApi } from '@/core/svc-client/onboarding';
import fetchFeatureFlags from '@/core/svc-client/post-feature-flags';

import ViewModule from '@/store/modules/ViewModule';
import Document from '@/store/modules/Document';
import Company from '@/store/modules/Company';
import Employee from '@/store/modules/Employee';
import FileHandler from '@/store/modules/FileHandler';

import { documentCompletion } from './composables/document-completion';
import documentsCompletion from './composables/documents-completion';
import documentsShape, { getValueKeyName } from './composables/documents-shape';
import { getAllFileUpload } from '@/core/svc-client/file-upload'; // TODO: FE fileUpload hack to provide full info to HR automated
import { getTenantSettings } from '@/core/svc-client/tenant-setting';
import getDocumentTypes from '@/core/svc-client/document-types-get';
import postSuperapiEmbedUrl from '@/core/svc-client/post-superapi-embed-url';
import getSuperapiOnboardingSession from '@/core/svc-client/get-superapi-onboarding-session';
import postSuperstepResetOnboardingSession from '@/core/svc-client/post-superstep-reset-onboarding-session';
import getForm from '@/core/svc-client/get-form';
import getForms from '@/core/svc-client/get-forms';
import setFormSubmission from '@/core/svc-client/set-form-submission';
import auth from '@/lib/request/svc-url/auth';
import jwtToObject from '@/lib/jwt-to-object';
import setGlobalApiEndpoint from '@/lib/set-global-api-endpoint';
import { docSignatureFileGet } from '@/core/svc-client/docsignature-file-get';
import getDocsignatureFileFromDms from '@/core/svc-client/get-docsignature-file';
import { BANK_DETAILS } from '@/core/field-names';
import mapErrorMessage from '@/lib/error-message-map';
import { COUNTRY } from '@/core/country';
import getFormFiles from '@/core/svc-client/get-form-files';
import { updateSignedStatus } from '@/core/svc-client/document-signature';
import { datadogLogs } from '@datadog/browser-logs';
import { isBugBountyEmail } from '@/core/validation/is-bug-bounty';
import { logWebHrCritical, logWebHrError } from '@/core/logger/log-wrapper';
import mockSuperAPIData from '@/fixtures/mock-super-api.json';
import {
  TFN_DECLARATION,
  TFN_RESIDENT_STATUS_VALUES,
} from '../core/field-names';
import { afterSaveHooks } from './after-save-hooks';
import { DOCUMENT_STATUSES, MUTATIONS } from '@/definitions';
import { DOCUMENT_NAMES } from '@/core/field-names';
import { FEATURE_FLAGS } from '@/core/feature-flags';
import { analytics } from '@/lib/analytics';

const mock = { data: { user: {}, company: {} } };

function isDeputyEmail(email) {
  return !!email && /@deputy\.com$/.test(email);
}

export const mutations = {
  setTenantId(state, newTenantId) {
    if (!newTenantId) {
      logWebHrError({
        message: 'TenantId must be provided',
        metadata: {
          initialTenantId: state.tenantId,
        },
      });
      return;
    }

    if (state.tenantId && newTenantId && newTenantId !== state.tenantId) {
      logWebHrCritical({
        message: 'attempt to update tenant id',
        metadata: {
          attemptingTenantId: newTenantId,
          initialTenantId: state.tenantId,
        },
        source: 'store',
      });

      return;
    }
    state.tenantId = newTenantId;
  },
  menuToggle(state, payload = undefined) {
    if (payload === true || payload === false) {
      state.menuState = payload;
      return true;
    }

    state.menuState = !state.menuState;
  },
  setDocuments(state, payload) {
    state.documents = payload;
  },
  setUser(state, payload) {
    state.user = payload;
  },
  setCompany(state, payload) {
    state.company = payload;
  },
  setDocument({ documents }, payload) {
    documents.splice(
      documents.findIndex((doc) => doc.name === payload.name),
      1,
      payload
    );
  },
  setError(state, payload) {
    state.error.push(payload);
  },
  setLanguage(state, payload) {
    state.locale = payload;
  },
  setOnboarding(state, payload) {
    state.onboarding = payload;
  },
  setOnboardingStatus(state, status) {
    state.onboardingStatus = status;
  },
  setFeatureFlags(state, payload) {
    state.featureFlags = payload;
  },
  setForms(state, payload) {
    state.forms = payload;

    state.indexedForms = payload.reduce((acc, form) => {
      acc[form.path] = form;
      return acc;
    }, {});
  },
  setSession(state, { token, jwt }) {
    state.session = token;
    state.onboardingId = token;
    state.token = jwt;
  },
  setSessionDms(state, { token, jwt }) {
    state.session = token;
    state.token = jwt;
  },
  setTenantSettings(state, payload) {
    state.tenantSettings = payload;
  },
  // TODO: review this solution
  setFieldRules(state, payload) {
    const fields = {};

    state.documents.forEach((currDocument) =>
      currDocument.fields.forEach((field) => (fields[field.name] = field))
    );

    // state.fields = fields;

    fields[payload.name]['appliedRules'] = payload.appliedRules;
  },
  setErrorListInDocuments(state, payload) {
    state.errorListInDocuments = payload;
  },
  setSuperfund(state, payload) {
    if (!state.documents) {
      return;
    }
    const doc = state.documents.find((doc) => doc.name === 'superannuation');
    if (!doc || !doc.fields) {
      return;
    }
    const field = doc.fields.find(
      (field) => field.name === 'superannuation.fund'
    );
    if (!field) {
      return;
    }
    if (payload === 'employerNominated') {
      field.value = field.defaultValue.ProductName;
      field.valueObj = field.defaultValue;
      field.superfundAUValue = field.defaultValue;
    } else if (payload === 'employeeNominated') {
      if (field.value === field.defaultValue.ProductName && !state.savedSuper) {
        field.value = '';
        field.valueObj = {};
        field.superfundAUValue = {};
      } else if (
        field.value === field.defaultValue.ProductName &&
        state.savedSuper
      ) {
        field.value = state.savedSuper.ProductName;
        field.valueObj = state.savedSuper;
        field.superfundAUValue = state.savedSuper;
      }
    }
    // Forces superfund field to update by updating the key
    state.theKey = state.theKey + 1;
  },
  setSavedSuper(state, payload) {
    // Hack to only set saved super when the type is employeeNominated.
    // TODO: remove savedSuper sate entirely from store
    const doc = state.documents?.find((doc) => doc.name === 'superannuation');
    const superType = doc?.fields?.find(
      (field) => field.name === 'superannuation.type'
    );
    if (superType?.value === 'employeeNominated') {
      state.savedSuper = payload;
    }
    if (superType?.value === 'selfManaged') {
      state.savedSuper = null;
    }
  },
  setOnboardingId(state, onboardingId) {
    state.onboardingId = onboardingId;
  },
  assignErrorMessagesPerFields(state, { error: e, documentName }) {
    if (!e?.message) {
      // not groupable error.
      return;
    }

    const errorArray = e.message.split(';');
    const newErrorFields = {};
    errorArray.forEach((error) => {
      let { fieldName, message } = mapErrorMessage(error.trim());
      if (!fieldName) {
        fieldName = documentName;
      }
      if (!(fieldName in newErrorFields)) {
        newErrorFields[fieldName] = [];
      }
      if (message && !newErrorFields[fieldName].includes(message)) {
        newErrorFields[fieldName].push(message);
      }
    });

    // replace existing errors with new errors
    Object.entries(newErrorFields).forEach(([key, value]) => {
      state.errorPerFields[key] = value;
    });
  },

  loadRelatedBackendErrors(state, documentName) {
    const relatedErrorKeys = Object.keys(state.errorPerFields).filter((item) =>
      item.includes(documentName)
    );

    state.errorListInDocuments = relatedErrorKeys.reduce((prev, key) => {
      return [...prev, ...state.errorPerFields[key]];
    }, []);
  },

  // The errors coming from BE are field errors, e.g. personalDetails.address.invalid
  // This method is currently used for AU/UK to show the warning icon in step card
  setDocumentBackendErrors(state, { error: e, currentDocument }) {
    if (!e?.message) {
      const unknownError = `${currentDocument.name};UnknownError`;
      state.documentErrors.push(unknownError);
      return;
    }

    if (!e.message.includes(currentDocument.name)) {
      state.documentErrors.push(`${currentDocument.name};${e.message}`);
      return;
    }
    //remove the blank entry from the string
    const errorString = e.message.replace(/;\s*$/, '');

    const errors = errorString.split(';').map((error) => error.trim());

    const filteredErrors = errors.filter((error) => {
      return !state.documentErrors.includes(error);
    });

    state.documentErrors = [...state.documentErrors, ...filteredErrors];
  },

  // This is an indexed array for all the BE errors per document
  // Currently, this is being used for US onboarding, but if we move AU/UK
  // to formio, we can use this to BE errors for that too
  setIndexedDocumentBackendErrors(
    state,
    { errors: errors, document: currDocument }
  ) {
    state.indexedDocumentErrors = {
      ...state.indexedDocumentErrors,
      [currDocument.name]: {
        errors: errors,
      },
    };
  },

  removeDocumentBackendErrors(state, currentDocument) {
    state.documentErrors = state.documentErrors.filter((error) => {
      return !error.includes(currentDocument.name);
    });
  },

  clearErrorMessageForDocument(state, documentName) {
    const relatedErrorKeys = Object.keys(state.errorPerFields).filter((item) =>
      item.includes(documentName)
    );
    relatedErrorKeys.forEach((key) => {
      state.errorPerFields[key] = [];
    });

    state.errorListInDocuments = [];
  },
  [MUTATIONS.SET_DOCUMENT_STATUSES](state, updates) {
    updates.forEach(({ documentName, status }) => {
      // TODO: convert documents to be indexed by name. Modifying getters are prevented by vuex.
      const currDocument = state.documents.find(
        (doc) => doc.name === documentName
      );
      if (currDocument) {
        currDocument.status = status;
      }
    });
  },

  setSuperapiOnboardingSession(state, payload) {
    state.superApiOnboardingSession = payload;
  },
  setSuperapiEmbed(state, payload) {
    state.superApiEmbed = payload;
  },
  setSuperApiEmbedCompleted(state) {
    if (!state.superApiOnboardingSession) {
      return;
    }
    state.superApiOnboardingSession.progression.completion_percentage = 1;
  },
  setLoadingSuperApiFailed(state, payload) {
    state.loadingSuperApiFailed = payload;
  },
  setLoadingSuperApi(state, payload) {
    state.loadingSuperApi = payload;
  },
  setNeedsSuperStepSessionReset(state, payload) {
    state.needsSuperStepSessionReset = payload;
  },
};
export const actions = {
  dispatchLoadTenantSettings: async function ({ commit }, tenantId) {
    try {
      const tenantSettings = await getTenantSettings(tenantId);
      commit('setTenantSettings', tenantSettings);
    } catch (e) {
      logWebHrError({
        message: 'Failed to load tenant settings',
        source: 'store',
        metadata: {
          tenantId,
        },
        error: e,
      });
      commit('setError', e);
      throw e;
    }
  },
  dispatchSetupAuth: async function ({ commit, state }, tempJwt, onboardingId) {
    if (!tempJwt) {
      throw new Error('missing temp JWT');
    }
    if (state.session) {
      return;
    }
    commit('setOnboardingId', onboardingId);
    const jwt = await auth(tempJwt);
    const jwtObject = jwtToObject(jwt);
    setGlobalApiEndpoint(jwtObject);
    commit('setSession', { token: state.onboardingId, jwt });
  },
  dispatchSetupAuthDms: async function (
    { commit, state },
    tempJwt,
    documentId
  ) {
    if (!tempJwt) {
      throw new Error('missing temp JWT');
    }
    if (state.session) {
      return;
    }

    const jwt = await auth(tempJwt);
    const jwtObject = jwtToObject(jwt);
    setGlobalApiEndpoint(jwtObject);
    commit('setSessionDms', { token: documentId, jwt });
  },
  // eslint-disable-next-line no-unused-vars
  getDocSignatureFile: async function ({ commit }, payload) {
    return await docSignatureFileGet(payload);
  },
  getFormFiles: async function (_, payload) {
    return await getFormFiles(payload);
  },
  // eslint-disable-next-line no-unused-vars
  getDocSignatureFromDms: async function ({ commit }, payload) {
    return await getDocsignatureFileFromDms(payload);
  },
  // eslint-disable-next-line no-unused-vars
  getForm: async function ({ commit, state }, documentDetails) {
    const payload = {
      tenantId: state.tenantId,
      onboardingId: state.onboardingId,
      ...documentDetails,
    };

    return await getForm(payload);
  },
  // eslint-disable-next-line no-unused-vars
  setSubmission: async function ({ commit, state }, payload) {
    const { documentId, submissionId, body } = payload;
    return await setFormSubmission(
      state.tenantId,
      state.onboardingId,
      documentId,
      body,
      submissionId
    );
  },
  getSuperEmbedUrl: async function ({ commit, state }) {
    try {
      const superapiEmbed = await postSuperapiEmbedUrl(
        state.superApiOnboardingSession.id
      );
      commit('setSuperapiEmbed', superapiEmbed);
    } catch (e) {
      commit('setLoadingSuperApiFailed', true);
    }
  },
  loadSuperApiData: async function ({ commit }, superStepOnboardingSessionId) {
    try {
      const superApiOnboardingSession = await getSuperapiOnboardingSession(
        superStepOnboardingSessionId
      );
      commit('setSuperapiOnboardingSession', superApiOnboardingSession);
      commit('setLoadingSuperApiFailed', false);
    } catch (e) {
      commit('setLoadingSuperApiFailed', true);
    }
  },
  analyticsIdentify: async function (_, onboarding) {
    if (!onboarding?.employee?.userId) {
      return;
    }
    try {
      analytics.identify(onboarding.employee.userId, {
        installationId: onboarding.company.id,
        installationUserID: onboarding.employee.userId,
        email: onboarding.employee.email,
        firstName: onboarding.employee.firstName,
        lastName: onboarding.employee.lastName,
        phone: onboarding.employee.mobile,
        deputyUUID: '',
        deputyEmailFlag: isDeputyEmail(onboarding.employee.email),
      });
    } catch (e) {
      logWebHrError({
        message: 'Failed to identify employee',
        source: 'store',
        error: e,
      });
    }
  },
  getDocuments: async function (
    { commit, state: storeState, getters: storeGetters, dispatch },
    onboardingId
  ) {
    // TODO: FE fileUpload hack to provide full info to HR automated
    let fileUploads = [];

    const tenantId = storeState.tenantId;

    // For me, this sounds really hacky solution.
    // TODO: investigate why this has to be called in here.
    try {
      fileUploads = await getAllFileUpload(onboardingId);
    } catch (e) {
      // First time onboarded fileUploads will always returns an error
      // panicking here will prevent user to onboard with web-hr
      logWebHrError({
        message: 'Failed to get all file uploads',
        source: 'store',
        error: e,
      });
    }

    try {
      const [featureFlags, onboarding, documentsData, documentTypes] =
        await Promise.all([
          fetchFeatureFlags([FEATURE_FLAGS.SUPER_API]),
          onboardingGetApi(onboardingId),
          svc.documents.get(onboardingId),
          getDocumentTypes(tenantId),
        ]);

      commit('setFeatureFlags', featureFlags);
      dispatch('analyticsIdentify', onboarding);

      const {
        employee,
        locale,
        company,
        state,
        defaults = {},
        superStepOnboardingSessionId,
      } = onboarding;
      let { documents } = onboarding;

      commit('setOnboarding', onboarding);

      // TODO: remove this mock data once the super API is enabled in BE
      if (storeGetters.isSuperAPIEnabled) {
        for (let index = 0; index < documents.length; index++) {
          const document = documents[index];

          if (document.name === DOCUMENT_NAMES.BANK_DETAILS) {
            documents.splice(index + 1, 0, ...mockSuperAPIData);
            break;
          }
        }

        await dispatch('loadSuperApiData', superStepOnboardingSessionId);

        // delete the superannuation document & tfn declaration document
        documents = documents.filter(
          (doc) =>
            doc.name !== DOCUMENT_NAMES.SUPERANNUATION &&
            doc.name !== DOCUMENT_NAMES.TFN_DECLARATION
        );
      }

      const isBounty = isBugBountyEmail(employee.email);

      datadogLogs.setGlobalContextProperty('company', company);
      datadogLogs.setGlobalContextProperty('isBounty', isBounty);

      commit('setOnboardingStatus', state);
      commit('setUser', employee);
      commit('Employee/set', employee);
      commit('setLanguage', locale);
      commit('setLanguage', locale);
      commit('setCompany', company);

      commit('Company/set', company);
      commit('Company/setDefaults', defaults);

      if (storeState.onboarding?.countryCode === COUNTRY.US) {
        const forms = await getForms(tenantId, onboardingId);
        commit('setForms', forms);
      }

      if (documents) {
        const doc = documents.find((doc) => doc.name === 'superannuation');
        if (doc && doc.fields) {
          const field = doc.fields.find(
            (field) => field.name === 'superannuation.fund'
          );
          if (field) {
            commit('setSavedSuper', Object.assign({}, field.superfundAUValue));
          }
        }
        commit(
          'setDocuments',
          documentsShape({
            documents,
            documentsData,
            documentTypes,
            fileUploads, // TODO: FE hack to provide full info to HR automated
            defaultData: defaults.documents,
            company,
            employee,
          })
        ); // TODO: split this code into the right files
        return true;
      }

      commit('setDocuments', []);
    } catch (e) {
      logWebHrError({
        message: 'Failed to load documents',
        source: 'store',
        metadata: {
          tenantId,
          onboardingId,
        },
        error: e,
      });
      commit('setError', e);
      throw e;
    }
  },
  async setDocument({ commit }, payload) {
    payload.toSave = true;
    commit('setDocument', payload);
  },
  // eslint-disable-next-line no-unused-vars
  async saveForm({ commit, state }, documentToSave) {
    try {
      commit('setDocument', documentToSave);
      return await svc.documents.save(state.onboardingId, documentToSave);
    } catch (e) {
      datadogLogs.logger.error(
        'Error saving form',
        {
          metaData: {
            submissionId: documentToSave.submissionId,
          },
        },
        e
      );
      throw e;
    }
  },
  async updateOnboardingStatus({ commit, state }, onboardingStatus) {
    try {
      commit('setOnboardingStatus', onboardingStatus);
      await svc.onboarding.status(state.onboardingId, onboardingStatus);
    } catch (e) {
      logWebHrError({
        message: 'Error updating status',
        source: 'store',
        metadata: {
          onboardingId: state.onboardingId,
          onboardingStatus,
        },
        error: e,
      });
      throw e;
    }
  },
  async saveSignature({ commit, state }, payload) {
    const { documentId, fieldId, signed, currentDocument } = payload;

    const field = currentDocument.fields.find((field) => field.id === fieldId);
    field.docSignatureEmbedValue.providerData.signed = signed;

    commit('setDocument', currentDocument);

    let signers = [];
    try {
      signers =
        JSON.parse(field.docSignatureEmbedValue.providerData.signers) || [];
    } catch (error) {
      signers = [];
      logWebHrError({
        message: 'Failed to parse signers json string in saveSignature',
        source: 'store/index.js',
        metadata: { providerData: field.docSignatureEmbedValue.providerData },
        error,
      });
    }

    if (signers.length === 1) {
      updateSignedStatus({
        onboardingId: state.onboardingId,
        documentId,
        fieldId,
        signed,
      });
    }
  },
  async saveDocuments(
    { commit, state },
    { id, documents, currentDocumentName }
  ) {
    const documentsToSave = [];
    const workRightsUploads = {};

    documents
      .filter(({ toSave }) => !!toSave)
      .forEach((currDocument) => {
        currDocument.toSave = false;
        commit('clearErrorMessageForDocument', currDocument.name);

        currDocument = executeTfnDeclarationRules(currDocument);

        commit('setDocument', currDocument);

        const documentToSave = {
          name: currDocument.name,
          fields: [],
          completion: currDocument.completion,
        };

        if (id) {
          documentToSave.id = currDocument.id;
        }

        currDocument.fields.forEach(async (field) => {
          const { id, name, type, value, valueObj, validation } = field; // eslint-disable-line no-unused-vars
          const returnData = { id, name, type };

          if (validation && typeof validation.beforeSave === 'function') {
            Object.assign(returnData, validation.beforeSave(field));
          } else {
            const valueKeyName = getValueKeyName(field.type);
            returnData[valueKeyName] =
              valueObj && valueObj.unformatted ? valueObj.unformatted : value;
          }
          if (name === 'superannuation.fund') {
            commit('setSavedSuper', Object.assign({}, field.valueObj));
          }

          // TODO: Remove this once the proper fix has been done
          if (name === BANK_DETAILS.BSB || name === BANK_DETAILS.SORT_CODE) {
            returnData.stringValue = returnData.stringValue
              ?.split(' ')
              .join(''); // remove spaces
          }

          if (name.includes('workRights')) {
            workRightsUploads[name] = field;
          }

          documentToSave.fields.push(returnData);
        });

        documentsToSave.push(documentToSave);
      });

    try {
      const responses = await svc.documents.save(id, documentsToSave);

      afterSaveHooks({ documents, countryCode: state.onboarding.countryCode });

      commit(
        MUTATIONS.SET_DOCUMENT_STATUSES,
        responses.map((docSaved) => ({
          documentName: docSaved.name,
          status: docSaved.status,
        }))
      );
    } catch (e) {
      commit('assignErrorMessagesPerFields', {
        error: e,
        documentName: currentDocumentName,
      });
      commit(
        MUTATIONS.SET_DOCUMENT_STATUSES,
        documentsToSave.map((docSaved) => ({
          documentName: docSaved.name,
          status: DOCUMENT_STATUSES.ERROR,
        }))
      );
      throw e;
    }
  },
  async resetSuperStepSession({ commit, state, dispatch }) {
    commit('setLoadingSuperApi', true);
    commit('setLoadingSuperApiFailed', false);
    commit('setNeedsSuperStepSessionReset', true);
    try {
      const { session } = await postSuperstepResetOnboardingSession(
        state.onboardingId
      );
      state.onboarding.superStepOnboardingSessionId = session.id;
      await dispatch('loadSuperApiData', session.id);
      await dispatch('getSuperEmbedUrl');
      commit('setNeedsSuperStepSessionReset', false);
    } catch (e) {
      commit('setLoadingSuperApiFailed', true);
    } finally {
      commit('setLoadingSuperApi', false);
    }
  },
  submit({ commit }, payload) {
    commit('submit', payload);
  },
  setLanguage({ commit }, payload) {
    commit('setLanguage', payload);
  },
  setSession({ commit }, payload) {
    commit('setSession', payload);
  },
  // TODO: review and split this code onto a module file
  setFieldRules({ commit }, payload) {
    commit('setFieldRules', payload);
  },
  setSuperfund({ commit }, payload) {
    // const doc = state.documents.find(doc => doc.name === 'superannuation');
    commit('setSuperfund', payload);
    // commit('setDocument', doc);
  },
  setSuperApiEmbedCompleted({ commit }) {
    commit('setSuperApiEmbedCompleted');
  },
};

const store = createStore({
  namespaced: true,
  plugins: [],
  modules: {
    View: ViewModule,
    Document: Document,
    Company: Company,
    Employee: Employee,
    FileHandler: FileHandler,
  },
  // TODO: re enable the strict mode on dev after deliver the v1
  // strict: (process.env.NODE_ENV !== 'production'),
  strict: false,
  state: {
    onboarding: {},
    onboardingId: null,
    onboardingStatus: null,
    documents: null,
    forms: [],
    indexedForms: {},
    company: mock.data.user || {},
    user: mock.data.user || {},
    // login,
    menuState: false,
    error: [],
    isDocumentsComplete: false,
    locale: 'en-AU',
    session: null,
    isDesktop: false,
    savedSuper: null,
    tenantSettings: {},

    // error message that is related to current document.
    errorListInDocuments: [],
    // Key value pair of the backend error per each fields.
    errorPerFields: {},

    theKey: 0,

    documentErrors: [],
    indexedDocumentErrors: {},
    tenantId: '',
    loadingSuperApi: false,
    loadingSuperApiFailed: false,
    needsSuperStepSessionReset: false,
    superApiEmbed: {
      url: '',
      expiresAt: null,
    },
  },

  getters: {
    shouldEnableSuperAPI: (state) => {
      return state.featureFlags?.find(
        (flag) => flag.name === FEATURE_FLAGS.SUPER_API
      )?.boolValue;
    },
    session: (state) => !!state.session,
    // TODO: review this code after update backend logic of email field and sections on documents
    documents: (state, getters) => {
      const { documents } = state;

      if (!documents) {
        return null;
      }

      // TODO: loop in a loop, get this code better. no harm for now because it's not many steps
      const docs = documents.map((currDocument) => {
        let result = currDocument;

        if (currDocument.name === DOCUMENT_NAMES.SUPER_API) {
          result.completion = getters.isSuperApiEmbedCompleted ? 100 : 0;
        } else if (currDocument.submissionId) {
          result.completion = 100;
        } else if (
          !currDocument.submissionId &&
          currDocument.fields.length === 0
        ) {
          result.completion = 0;
        } else {
          result = documentCompletion(currDocument);
        }

        return result;
      });

      return docs;
    },
    isDocumentsComplete: (state, getters) => {
      const documents = getters.documents;

      if (!documents || documents.length === 0) {
        return false;
      }

      let totals = 0;
      let documentsLength = 0;

      documents.forEach((doc) => {
        if (doc.completion >= 0) {
          documentsLength += 1;
          totals += doc.completion;
        }
      });

      return totals >= documentsLength * 100;
    },
    // TODO: review this completion loop try to get it from each of the documents too much recursivity
    completion({ documents }) {
      return documentsCompletion(documents);
    },
    state: ({ onboarding }) => onboarding.state,
    onboardingStatus: (state) => state.onboardingStatus,
    indexedDocuments: (_, getters) => {
      if (!getters.documents || getters.documents.length === 0) {
        return {};
      }
      return getters.documents.reduce((acc, doc) => {
        acc[doc.name] = doc;
        return acc;
      }, {});
    },
    indexedDocumentSubmissionStatuses: (_, getters) => {
      if (!getters.documents) {
        return {};
      }
      return getters.documents.reduce((acc, doc) => {
        acc[doc.name] = doc.status;
        return acc;
      }, {});
    },
    isSuperAPIEnabled: (state, getters) => {
      return (
        getters.shouldEnableSuperAPI &&
        state.onboarding?.countryCode === COUNTRY.AU &&
        !!state.onboarding?.employee?.businessNumber &&
        !!state.onboarding.superStepOnboardingSessionId
      );
    },
    isSuperapiEmbedUrlExpired(state) {
      if (!state.superApiEmbed?.expiresAt) {
        return false;
      }

      try {
        return new Date(state.superApiEmbed.expiresAt).getTime() < Date.now();
      } catch (e) {
        logWebHrError({
          message: 'Error checking if super api embed url is expired',
          source: 'store',
          metadata: state.superApiEmbed,
          error: e,
        });
        return false;
      }
    },
    isSuperApiEmbedCompleted(state) {
      return (
        state?.superApiOnboardingSession?.progression?.completion_percentage ===
        1
      );
    },
  },

  mutations: mutations,

  actions: actions,
});

function getFields(currDocument, fieldNames) {
  const fields = {};
  currDocument.fields.forEach((field, index) => {
    if (fieldNames.indexOf(field.name) > -1) {
      fields[field.name] = { field, index };
    }
  });
  return fields;
}

function executeTfnDeclarationRules(currDocument) {
  if (currDocument.name !== 'tfnDeclaration') {
    return currDocument;
  }

  const fields = getFields(currDocument, [
    TFN_DECLARATION.OPTION,
    TFN_DECLARATION.TFN,
    TFN_DECLARATION.REASON,
    TFN_DECLARATION.RESIDENT_STATUS,
    TFN_DECLARATION.COUNTRY_OF_ORIGIN,
    TFN_DECLARATION.TAX_FREE_THRESHOLD,
    TFN_DECLARATION.HELP_LOAN_OR_STUDENT_DEBT,
  ]);

  if (
    !fields[TFN_DECLARATION.OPTION] ||
    !fields[TFN_DECLARATION.TFN] ||
    !fields[TFN_DECLARATION.REASON] ||
    !fields[TFN_DECLARATION.RESIDENT_STATUS] ||
    !fields[TFN_DECLARATION.COUNTRY_OF_ORIGIN]
  ) {
    return currDocument;
  }

  if (
    fields[TFN_DECLARATION.RESIDENT_STATUS].field.value !==
    TFN_RESIDENT_STATUS_VALUES.WORKING_HOLIDAY
  ) {
    fields[TFN_DECLARATION.COUNTRY_OF_ORIGIN].field.value = '';
  }

  if (
    [
      TFN_RESIDENT_STATUS_VALUES.FOREIGN,
      TFN_RESIDENT_STATUS_VALUES.WORKING_HOLIDAY,
    ].includes(fields[TFN_DECLARATION.RESIDENT_STATUS].field.value)
  ) {
    fields[TFN_DECLARATION.TAX_FREE_THRESHOLD].field.value = 'no';
    fields[TFN_DECLARATION.HELP_LOAN_OR_STUDENT_DEBT].field.value = 'no';
  }
  if (fields[TFN_DECLARATION.OPTION].field.value === 'Yes') {
    fields[TFN_DECLARATION.REASON].field.value = '';
  } else if (fields[TFN_DECLARATION.OPTION].field.value === 'No') {
    fields[TFN_DECLARATION.TFN].field.value = '';
  }

  return currDocument;
}

// Export helper functions for testing
export { executeTfnDeclarationRules };

export default store;
